import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import { MdContentCopy, MdThumbUp, MdThumbDown, MdMic, MdKeyboardArrowDown } from 'react-icons/md';
import { FaArrowRightLong } from 'react-icons/fa6';
import './ChatArea.css';
import { useChat } from '../../context/ChatContext';

const ChatArea = ({ 
  messages = [], // Provide default empty array 
  loading,
  isStreaming,
  onSendMessage, 
  currentBotId, 
  currentScreenId,
  onSwitchBot,
  screens 
}) => {
  const [input, setInput] = useState('');
  const [hoveredUserMsg, setHoveredUserMsg] = useState(null);
  const [feedback, setFeedback] = useState({}); // { [messageIdx]: 'thanks' }
  const [copied, setCopied] = useState({}); // { [messageIdx]: true }
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const messagesEndRef = useRef(null);
  const messageStartRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const userMessagesStartRef = useRef(null);
  const { currentChatId, updateAssistantMessageContent } = useChat();

  // Dropdown examples data
  const dropdownExamples = {
    'dosecalc': [
      "Fentanyl dose for intraoperative analgesia in a 30kg pediatric patient.",
      "Midazolam premedication for a 60-year-old male with liver disease."
    ],
    'case-manager': [
      "65-year-old male, elective total knee replacement, history of hypertension and diabetes.",
      "45-year-old female, laparoscopic cholecystectomy, BMI 42."
    ],
    'search': [
      "Best practices for difficult airway management in obese patients.",
      "How to manage perioperative beta-blockers in non-cardiac surgery."
    ]
  };

  // Handle dropdown toggle
  const toggleDropdown = (botId) => {
    setOpenDropdown(openDropdown === botId ? null : botId);
  };

  // Handle example selection
  const handleExampleSelect = (example) => {
    setInput(example);
    setOpenDropdown(null);
    // Send the example to chat immediately
    onSendMessage(example);
    setInput('');
  };

  // Add effect to scroll to last message when a new AI message is added
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === 'assistant' && messageStartRef.current) {
      messageStartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (lastMsg.role === 'user' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.action-btn-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    onSendMessage(input);
    setInput('');
  };

  // Copy to clipboard handler
  const handleCopy = (content, idx) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(content);
    } else {
      // fallback
      const textarea = document.createElement('textarea');
      textarea.value = content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(prev => ({ ...prev, [idx]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [idx]: false })), 1200);
  };

  // Like/dislike handler
  const handleFeedback = (idx) => {
    setFeedback(prev => ({ ...prev, [idx]: true }));
    setTimeout(() => setFeedback(prev => ({ ...prev, [idx]: false })), 1500);
  };

  // Function to set hovered message index
  const setHoveredMessage = (idx) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredUserMsg(idx);
  };

  // Function to handle mouse leave with timeout
  const handleMouseLeave = () => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Set new timeout
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredUserMsg(null);
      hoverTimeoutRef.current = null;
    }, 3000);
  };

  // Use the currentScreenId prop if available, otherwise compute it
  const screenId = currentScreenId || (() => {
    if (!screens || !currentBotId) return 'case-manager';
    
    const screen = screens.find(s => s.botId === currentBotId);
    return screen ? screen.id : (screens.length > 0 ? screens[0].id : 'case-manager');
  })();

  // Loading state for screens
  if (!screens) {
    return (
      <div className="chat-area-loading">
        <div className="spinner"></div>
        <div className="loading-text">Loading chat interface</div>
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="empty-center-wrapper">
        <div className="empty-chat-headline">
          {screenId === 'case-manager' && 'Enter age, surgery, comorbidities, or labs for an anesthesia plan.'}
          {screenId === 'dosecalc' && 'Type medication for recommended doses.'}
          {screenId === 'search' && 'Enter a topic for concise evidence-based summary.'}
        </div>
        <form className="input-card" onSubmit={handleSubmit}>
          <div className="input-row">
            <input
              type="text"
              placeholder="Ask me anything..."
              className="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
            />
            <button type="submit" className="send-btn" disabled={loading || !input.trim()}>
              {loading ? (
                <span className="button-loading">
                  <div className="spinner-small"></div>
                </span>
              ) : (
                  <FaArrowRightLong size={16} />
              )}
            </button>
          </div>
          <div className="tab-buttons">
            {screens.map(bot => (
              <button
                type="button"
                key={bot.botId}
                className={`tab-btn${screenId === bot.id ? ' active' : ''}`}
                disabled={loading}
                onClick={() => onSwitchBot(bot.id)}
              >
                <div className="tab-btn-content">
                  {bot.botImg}
                  {bot.label}
                </div>
              </button>
            ))}
          </div>
        </form>
        <div className={`action-buttons ${openDropdown ? 'expanded' : ''}`}>
          <div className="action-btn-container">
            <button className={`action-btn ${openDropdown === 'dosecalc' ? 'expanded' : ''}`} onClick={() => toggleDropdown('dosecalc')}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_679_614)"><path d="M14.6667 6.41643L14.6456 5.33752C15.2607 5.12714 15.7811 4.70511 16.114 4.14668C16.4468 3.58825 16.5705 2.92974 16.4629 2.28859C16.3553 1.64745 16.0235 1.06536 15.5266 0.646151C15.0297 0.226938 14.4001 -0.00214228 13.75 -0.000232293H8.25C7.60208 -0.00146001 6.97462 0.22666 6.47878 0.643725C5.98293 1.06079 5.65069 1.63988 5.5409 2.27844C5.4311 2.91699 5.55084 3.5738 5.87891 4.13253C6.20698 4.69126 6.72221 5.11585 7.33333 5.3311V6.41643C6.1182 6.41789 4.95326 6.90124 4.09403 7.76047C3.23481 8.6197 2.75146 9.78464 2.75 10.9998V17.4164C2.75146 18.6316 3.23481 19.7965 4.09403 20.6557C4.95326 21.515 6.1182 21.9983 7.33333 21.9998H14.6667C15.8818 21.9983 17.0467 21.515 17.906 20.6557C18.7652 19.7965 19.2485 18.6316 19.25 17.4164V10.9998C19.2485 9.78464 18.7652 8.6197 17.906 7.76047C17.0467 6.90124 15.8818 6.41789 14.6667 6.41643ZM8.25 1.8331H13.75C13.9931 1.8331 14.2263 1.92968 14.3982 2.10159C14.5701 2.27349 14.6667 2.50665 14.6667 2.74977C14.6667 2.99288 14.5701 3.22604 14.3982 3.39795C14.2263 3.56986 13.9931 3.66643 13.75 3.66643H8.25C8.00689 3.66643 7.77373 3.56986 7.60182 3.39795C7.42991 3.22604 7.33333 2.99288 7.33333 2.74977C7.33333 2.50665 7.42991 2.27349 7.60182 2.10159C7.77373 1.92968 8.00689 1.8331 8.25 1.8331ZM17.4167 17.4164C17.4167 18.1458 17.1269 18.8453 16.6112 19.361C16.0955 19.8767 15.396 20.1664 14.6667 20.1664H7.33333C6.60399 20.1664 5.90451 19.8767 5.38879 19.361C4.87306 18.8453 4.58333 18.1458 4.58333 17.4164V10.9998C4.58333 10.2704 4.87306 9.57095 5.38879 9.05522C5.90451 8.5395 6.60399 8.24977 7.33333 8.24977H8.25C8.49312 8.24977 8.72627 8.15319 8.89818 7.98128C9.07009 7.80937 9.16667 7.57622 9.16667 7.3331V5.49977H12.8086L12.8333 7.34593C12.8367 7.58682 12.9348 7.81669 13.1063 7.98584C13.2779 8.15498 13.5091 8.24979 13.75 8.24977H14.6667C15.396 8.24977 16.0955 8.5395 16.6112 9.05522C17.1269 9.57095 17.4167 10.2704 17.4167 10.9998V17.4164ZM14.6667 13.7498C14.6667 13.9929 14.5701 14.226 14.3982 14.3979C14.2263 14.5699 13.9931 14.6664 13.75 14.6664H11.9167V16.4998C11.9167 16.7429 11.8201 16.976 11.6482 17.148C11.4763 17.3199 11.2431 17.4164 11 17.4164C10.7569 17.4164 10.5237 17.3199 10.3518 17.148C10.1799 16.976 10.0833 16.7429 10.0833 16.4998V14.6664H8.25C8.00689 14.6664 7.77373 14.5699 7.60182 14.3979C7.42991 14.226 7.33333 13.9929 7.33333 13.7498C7.33333 13.5067 7.42991 13.2735 7.60182 13.1016C7.77373 12.9297 8.00689 12.8331 8.25 12.8331H10.0833V10.9998C10.0833 10.7567 10.1799 10.5235 10.3518 10.3516C10.5237 10.1797 10.7569 10.0831 11 10.0831C11.2431 10.0831 11.4763 10.1797 11.6482 10.3516C11.8201 10.5235 11.9167 10.7567 11.9167 10.9998V12.8331H13.75C13.9931 12.8331 14.2263 12.9297 14.3982 13.1016C14.5701 13.2735 14.6667 13.5067 14.6667 13.7498Z" fill="#132775"/></g><defs><clipPath id="clip0_679_614"><rect width="22" height="22" fill="white"/></clipPath></defs></svg>
              <span>Enter the medication to view recommended dosages.</span>
              <MdKeyboardArrowDown size={16} className={`dropdown-arrow ${openDropdown === 'dosecalc' ? 'rotated' : ''}`} />
            </button>
            {openDropdown === 'dosecalc' && (
              <div className="expanded-examples">
                {dropdownExamples['dosecalc'].map((example, index) => (
                  <button
                    key={index}
                    className="example-item"
                    onClick={() => handleExampleSelect(example)}
                  >
                    {example}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="action-btn-container">
            <button className={`action-btn ${openDropdown === 'case-manager' ? 'expanded' : ''}`} onClick={() => toggleDropdown('case-manager')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 0H8C6.67392 0 5.40215 0.526784 4.46447 1.46447C3.52678 2.40215 3 3.67392 3 5V19C3 19.6566 3.12933 20.3068 3.3806 20.9134C3.63188 21.52 4.00017 22.0712 4.46447 22.5355C5.40215 23.4732 6.67392 24 8 24H16C16.6566 24 17.3068 23.8707 17.9134 23.6194C18.52 23.3681 19.0712 22.9998 19.5355 22.5355C19.9998 22.0712 20.3681 21.52 20.6194 20.9134C20.8707 20.3068 21 19.6566 21 19V5C21 3.67392 20.4732 2.40215 19.5355 1.46447C18.5979 0.526784 17.3261 0 16 0V0ZM19 19C19 19.7956 18.6839 20.5587 18.1213 21.1213C17.5587 21.6839 16.7956 22 16 22H8C7.20435 22 6.44129 21.6839 5.87868 21.1213C5.31607 20.5587 5 19.7956 5 19V5C5 4.20435 5.31607 3.44129 5.87868 2.87868C6.44129 2.31607 7.20435 2 8 2H16C16.7956 2 17.5587 2.31607 18.1213 2.87868C18.6839 3.44129 19 4.20435 19 5V19Z" fill="#132876"/><path d="M14.4167 10.75H12.5833V8.91667C12.5833 8.67355 12.4867 8.44039 12.3148 8.26849C12.1429 8.09658 11.9098 8 11.6667 8C11.4235 8 11.1904 8.09658 11.0185 8.26849C10.8466 8.44039 10.75 8.67355 10.75 8.91667V10.75H8.91667C8.67355 10.75 8.44039 10.8466 8.26849 11.0185C8.09658 11.1904 8 11.4235 8 11.6667C8 11.9098 8.09658 12.1429 8.26849 12.3148C8.44039 12.4867 8.67355 12.5833 8.91667 12.5833H10.75V14.4167C10.75 14.6598 10.8466 14.8929 11.0185 15.0648C11.1904 15.2367 11.4235 15.3333 11.6667 15.3333C11.9098 15.3333 12.1429 15.2367 12.3148 15.0648C12.4867 14.8929 12.5833 14.6598 12.5833 14.4167V12.5833H14.4167C14.6598 12.5833 14.8929 12.4867 15.0648 12.3148C15.2367 12.1429 15.3333 11.9098 15.3333 11.6667C15.3333 11.4235 15.2367 11.1904 15.0648 11.0185C14.8929 10.8466 14.6598 10.75 14.4167 10.75Z" fill="#132876"/></svg>            
              <span>Provide age, procedure, medical history, or lab results to generate an anesthesia plan.</span>
              <MdKeyboardArrowDown size={16} className={`dropdown-arrow ${openDropdown === 'case-manager' ? 'rotated' : ''}`} />
            </button>
            {openDropdown === 'case-manager' && (
              <div className="expanded-examples">
                {dropdownExamples['case-manager'].map((example, index) => (
                  <button
                    key={index}
                    className="example-item"
                    onClick={() => handleExampleSelect(example)}
                  >
                    {example}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="action-btn-container">
            <button className={`action-btn ${openDropdown === 'search' ? 'expanded' : ''}`} onClick={() => toggleDropdown('search')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M23.7068 22.2937L17.7378 16.3247C19.3644 14.3353 20.1642 11.7968 19.9716 9.23426C19.7791 6.67173 18.609 4.28123 16.7034 2.55722C14.7977 0.833208 12.3024 -0.0924103 9.73342 -0.0281784C7.16447 0.0360534 4.71849 1.08522 2.9014 2.90231C1.08431 4.7194 0.0351379 7.16539 -0.029094 9.73434C-0.0933258 12.3033 0.832293 14.7987 2.5563 16.7043C4.28031 18.6099 6.67081 19.78 9.23334 19.9725C11.7959 20.1651 14.3344 19.3653 16.3238 17.7387L22.2928 23.7077C22.4814 23.8899 22.734 23.9907 22.9962 23.9884C23.2584 23.9861 23.5092 23.8809 23.6946 23.6955C23.88 23.5101 23.9852 23.2593 23.9875 22.9971C23.9897 22.7349 23.8889 22.4823 23.7068 22.2937ZM9.99978 18.0007C8.41753 18.0007 6.87081 17.5315 5.55522 16.6525C4.23963 15.7734 3.21425 14.524 2.60875 13.0622C2.00324 11.6004 1.84482 9.99182 2.1535 8.43998C2.46218 6.88813 3.22411 5.46266 4.34293 4.34384C5.46175 3.22502 6.88721 2.4631 8.43906 2.15442C9.99091 1.84573 11.5994 2.00416 13.0613 2.60966C14.5231 3.21516 15.7725 4.24054 16.6515 5.55614C17.5306 6.87173 17.9998 8.41845 17.9998 10.0007C17.9974 12.1217 17.1538 14.1551 15.654 15.6549C14.1542 17.1547 12.1208 17.9983 9.99978 18.0007Z" fill="#132876"/><path d="M13 8.99999H11V7C11 6.73478 10.8946 6.48043 10.7071 6.29289C10.5196 6.10536 10.2652 6 9.99999 6C9.73478 6 9.48042 6.10536 9.29289 6.29289C9.10535 6.48043 8.99999 6.73478 8.99999 7V8.99999H7C6.73478 8.99999 6.48043 9.10535 6.29289 9.29289C6.10536 9.48042 6 9.73478 6 9.99999C6 10.2652 6.10536 10.5196 6.29289 10.7071C6.48043 10.8946 6.73478 11 7 11H8.99999V13C8.99999 13.2652 9.10535 13.5196 9.29289 13.7071C9.48042 13.8946 9.73478 14 9.99999 14C10.2652 14 10.5196 13.8946 10.7071 13.7071C10.8946 13.5196 11 13.2652 11 13V11H13C13.2652 11 13.5196 10.8946 13.7071 10.7071C13.8946 10.5196 14 10.2652 14 9.99999C14 9.73478 13.8946 9.48042 13.7071 9.29289C13.5196 9.10535 13.2652 8.99999 13 8.99999Z" fill="#132876"/></svg>            
              <span>Type a topic to get a brief, evidence-based summary.</span>
              <MdKeyboardArrowDown size={16} className={`dropdown-arrow ${openDropdown === 'search' ? 'rotated' : ''}`} />
            </button>
            {openDropdown === 'search' && (
              <div className="expanded-examples">
                {dropdownExamples['search'].map((example, index) => (
                  <button
                    key={index}
                    className="example-item"
                    onClick={() => handleExampleSelect(example)}
                  >
                    {example}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="messages">
        {messages.map((msg, idx) => {
          // Don't render assistant messages with empty content
          if (msg.role === 'assistant' && !msg.content.trim()) {
            return null;
          }
          return (
          <React.Fragment key={idx}>
            {msg.role === 'user' ? (
              <div
                className={`message ${msg.role === 'user' ? 'user' : 'ai'}`}
                ref={userMessagesStartRef}
              > 
                <div ref={messageStartRef} onMouseEnter={() => setHoveredMessage(idx)}
                onMouseLeave={() => handleMouseLeave()} className="user-message-content">{msg.content}</div>
              </div>
            ) : (
              <div className="ai-message-wrapper">
                <div className="ai-avatar-wrapper">
                  <img src={process.env.PUBLIC_URL + '/faviconM.svg'} alt="AI Avatar" className="ai-avatar" />
                </div>
                <div
                  className={`message ${msg.role === 'user' ? 'user' : 'ai'}`}
                > 
                  <div className="markdown-content">
                    <Markdown components={{
                      p: ({node, ...props}) => <p className="markdown-paragraph" {...props} />,
                      ul: ({node, ...props}) => <ul className="markdown-list" {...props} />,
                      ol: ({node, ...props}) => <ol className="markdown-ordered-list" {...props} />,
                      li: ({node, ...props}) => <li className="markdown-list-item" {...props} />,
                      h1: ({node, ...props}) => <h1 className="markdown-heading" {...props} />,
                      h2: ({node, ...props}) => <h2 className="markdown-heading" {...props} />,
                      h3: ({node, ...props}) => <h3 className="markdown-heading" {...props} />,
                      hr: ({node, ...props}) => <hr className="markdown-divider" {...props} />,
                      blockquote: ({node, ...props}) => <blockquote className="markdown-blockquote" {...props} />,
                      pre: ({node, ...props}) => <pre className="markdown-code-block" {...props} />
                    }}>
                      {msg.content}
                    </Markdown>
                  </div>
                </div>
              </div>
            )}
            <div className="message-actions-row">
              <div className="copy-btn-wrapper">
                {((msg.role === 'user' && hoveredUserMsg === idx) || msg.role === 'assistant' && !isStreaming) && (
                  <button
                    className="copy-btn"
                    title="Copy message"
                    onClick={() => handleCopy(msg.content, idx)}
                    tabIndex={0}
                  >
                    <MdContentCopy size={18} />
                  </button>
                )}
                {copied[idx] && (
                  <span className="copied-tooltip">Copied!</span>
                )}
              </div>
              {msg.role === 'assistant' && !isStreaming && (
                <>
                  <button
                    className="like-btn"
                    title="Like"
                    onClick={() => handleFeedback(idx)}
                    tabIndex={0}
                    disabled={feedback[idx]}
                  >
                    <MdThumbUp size={18} />
                  </button>
                  <button
                    className="dislike-btn"
                    title="Dislike"
                    onClick={() => handleFeedback(idx)}
                    tabIndex={0}
                    disabled={feedback[idx]}
                  >
                    <MdThumbDown size={18} />
                  </button>
                  {feedback[idx] && (
                    <span className="feedback-thanks">Thank you for your feedback!</span>
                  )}
                </>
              )}
            </div>
            <div className='message-actions-row-spacer' ref={messagesEndRef}/>
          </React.Fragment>
          );
        })}
        {loading && (
          <div className="indicator" ref={messagesEndRef}>
            <div className="typing-indicator-container">
              <span className="typing-indicator">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </span>
            </div>
          </div>
        )}
      </div>
      <form className="input-card" onSubmit={handleSubmit}>
          <div className="input-row">
            <input
              type="text"
              placeholder="Ask me anything..."
              className="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
            />
            <button type="submit" className="send-btn" disabled={loading || !input.trim()}>
              {loading ? (
                <span className="button-loading">
                  <div className="spinner-small"></div>
                </span>
              ) : (
                  <FaArrowRightLong size={16} />
              )}
            </button>
          </div>
          <div className="tab-buttons">
            {screens.map(bot => (
              <button
                type="button"
                key={bot.botId}
                className={`tab-btn${screenId === bot.id ? ' active' : ''}`}
                disabled={loading}
                onClick={() => onSwitchBot(bot.id)}
              >
                <div className="tab-btn-content">
                  {bot.botImg}
                  {bot.label}
                </div>    
              </button>
            ))}
          </div>
        </form>
        <div className="disclaimer-text align-self-center py-2">
          MAIA is informational, not a substitute for professional judgement.
        </div>
    </>
  );
};

export default ChatArea; 